<template>
  <v-app>
    <v-main>
      <v-container
        fluid
        class="fill-height"
        style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
      >
        <v-row align="center" justify="center">
          <v-col cols="12" sm="8" md="5" lg="4" xl="3">
            <v-card class="elevation-12 rounded-lg">
              <v-toolbar color="primary" dark flat class="rounded-t-lg">
                <v-toolbar-title class="text-h5">
                  <v-icon class="mr-2">mdi-lock-outline</v-icon>
                  SSO Authentication
                </v-toolbar-title>
              </v-toolbar>

              <v-card-text class="pa-6">
                <v-alert type="info" variant="tonal" class="mb-4" density="compact">
                  <v-icon class="mr-2">mdi-information-outline</v-icon>
                  Please verify your JAMS credentials to connect with WordPress
                </v-alert>

                <v-form @submit.prevent="handleSSHAuthentication">
                  <v-text-field
                    v-model="username"
                    :disabled="isLoading"
                    label="Username"
                    prepend-inner-icon="mdi-account"
                    variant="outlined"
                    required
                    class="mb-3"
                  ></v-text-field>

                  <v-text-field
                    v-model="password"
                    :disabled="isLoading"
                    :type="showPassword ? 'text' : 'password'"
                    label="Password"
                    prepend-inner-icon="mdi-lock"
                    :append-inner-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                    @click:append-inner="showPassword = !showPassword"
                    variant="outlined"
                    required
                    @keyup.enter="handleSSHAuthentication"
                  ></v-text-field>

                  <v-alert
                    v-if="errorMessage"
                    type="error"
                    variant="tonal"
                    class="mb-3"
                    density="compact"
                  >
                    {{ errorMessage }}
                  </v-alert>
                </v-form>
              </v-card-text>

              <v-card-actions class="pa-6 pt-0">
                <v-btn
                  color="primary"
                  variant="flat"
                  size="large"
                  block
                  :disabled="!username || !password || isLoading"
                  :loading="isLoading"
                  @click="handleSSHAuthentication"
                >
                  <v-icon class="mr-2">mdi-login-variant</v-icon>
                  Authenticate & Connect
                </v-btn>

                <v-btn
                  v-if="redirectUrl"
                  color="grey"
                  variant="text"
                  size="small"
                  block
                  class="mt-2"
                  @click="cancelSSO"
                  :disabled="isLoading"
                >
                  Cancel
                </v-btn>
              </v-card-actions>
            </v-card>

            <div class="text-center mt-4">
              <p class="text-body-2" style="color: rgba(255, 255, 255, 0.8)">
                This authentication is required to establish a secure connection between JAMS and
                WordPress.
              </p>
              <p class="text-caption mt-2" style="color: rgba(255, 255, 255, 0.6)">
                This process is independent of your current JAMS session.
              </p>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </v-main>

    <!-- Success Dialog -->
    <v-dialog v-model="showSuccess" persistent max-width="400">
      <v-card>
        <v-card-title class="text-h6">
          <v-icon color="success" class="mr-2">mdi-check-circle</v-icon>
          Authentication Successful
        </v-card-title>
        <v-card-text>
          <p>Your JAMS account has been verified. Redirecting to WordPress...</p>
          <v-progress-linear indeterminate color="primary" class="mt-3"></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { authenticateForSSO, authenticateForSSOClient } from '@/services/authSignin'

const router = useRouter()
const route = useRoute()

const username = ref('')
const password = ref('')
const showPassword = ref(false)
const isLoading = ref(false)
const errorMessage = ref('')
const showSuccess = ref(false)
const redirectUrl = ref('')
const userType = ref('company')

onMounted(() => {
  redirectUrl.value = route.query.redirect_url || ''
  userType.value = route.query.user_type || 'company'

  if (!redirectUrl.value) {
    errorMessage.value = 'Invalid SSO request. No redirect URL provided.'
    return
  }

  console.log(
    'SSO Re-authentication initiated for WordPress connection, user_type:',
    userType.value
  )
})

const handleSSHAuthentication = async () => {
  if (!username.value || !password.value) {
    errorMessage.value = 'Please enter both username and password'
    return
  }

  isLoading.value = true
  errorMessage.value = ''

  try {
    console.log('SSO Authentication - Starting authentication with Cognito...')
    console.log('Username:', username.value, 'User Type:', userType.value)

    let session
    if (userType.value === 'client') {
      session = await authenticateForSSOClient(username.value, password.value)
    } else {
      session = await authenticateForSSO(username.value, password.value)
    }

    if (!session) {
      throw new Error('Authentication failed - no session returned')
    }

    const refreshToken = session.getRefreshToken()?.getToken()
    const accessToken = session.getAccessToken()
    const role =
      accessToken?.payload?.['cognito:groups']?.[0] ||
      (userType.value === 'client' ? 'client_user' : 'company_user')

    console.log('SSO Authentication successful!')
    console.log('Role:', role)
    console.log('Has refresh token:', !!refreshToken)

    if (!refreshToken) {
      throw new Error('No refresh token received from authentication')
    }

    showSuccess.value = true

    const wpCallbackUrl = new URL(decodeURIComponent(redirectUrl.value))
    wpCallbackUrl.searchParams.append('username', username.value)
    wpCallbackUrl.searchParams.append('refresh_token', refreshToken)
    wpCallbackUrl.searchParams.append('role', role)
    wpCallbackUrl.searchParams.append('user_type', userType.value)

    console.log('Redirecting to WordPress with SSO tokens...')

    setTimeout(() => {
      window.location.href = wpCallbackUrl.toString()
    }, 1500)
  } catch (error) {
    console.error('SSO Authentication Error:', error)
    errorMessage.value = error.message || 'Authentication failed. Please check your credentials.'
    isLoading.value = false
    showSuccess.value = false
  }
}

const cancelSSO = () => {
  // Simply close the window or go back to the previous page
  // Since this is opened from WordPress, we can close the window
  if (window.opener) {
    // If opened in a new window/tab, close it
    window.close()
  } else {
    // Otherwise navigate back to the main login page
    router.push('/company/login')
  }
}
</script>

<style scoped>
.v-container {
  min-height: 100vh;
}
</style>
